// HQ's sales pipeline (F-HQ-39; spec/14 §16, spec/07 §7): the cafés Clavian is trying to sign, how far each has got, and
// what to do next. A light pipeline, not a CRM.
//
//   GET    /v1/hq/leads                              the pipeline, filtered and sorted            hq:leads
//   POST   /v1/hq/leads                              add a lead                                   hq:leads
//   POST   /v1/hq/leads/import                       import the sales spreadsheet (dry run first) hq:leads
//   GET    /v1/hq/leads/cities                       cities with counts, for filter chips         hq:leads
//   GET    /v1/hq/leads/queue                        Today: follow-ups due, new leads to contact  hq:leads
//   GET    /v1/hq/leads/funnel                       leads by stage and city, replies by template hq:leads
//   GET    /v1/hq/leads/assignees                    HQ members who can own a lead                hq:leads
//   GET    /v1/hq/leads/{leadId}                     one lead                                     hq:leads
//   PATCH  /v1/hq/leads/{leadId}                     edit                                         hq:leads
//   DELETE /v1/hq/leads/{leadId}                     erase it with its timeline                   hq:leads
//   POST   /v1/hq/leads/{leadId}/stage               change stage                                 hq:leads
//   GET    /v1/hq/leads/{leadId}/activities          the timeline, newest first                   hq:leads
//   POST   /v1/hq/leads/{leadId}/activities          log a note, call, visit or reply             hq:leads
//   POST   /v1/hq/leads/{leadId}/outreach            record that a message was opened             hq:leads
//   POST   /v1/hq/leads/{leadId}/convert             make it an organisation                      hq:leads + hq:organizations
//   GET/POST /v1/hq/lead-templates, PATCH …/{templateId}, POST …/{templateId}/archive            hq:leads
//
// **HQ never sends.** Opening outreach records the message, and returns the WhatsApp link (or the Instagram address) the
// member opens in their own app; nothing is sent from Clavian's servers.
//
// Platform scope: every route needs a platform role holding `hq:leads` and a second factor on the session.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { LeadActivityIdSchema, LeadIdSchema, LeadTemplateIdSchema, OrganizationIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_LEADS } from "../permissions/index.js";
import { PageQuerySchema, PageSchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { HqOrganizationSchema, OrganizationKindSchema, OwnerInvitationContactSchema } from "./hq-organizations.js";
import { CreatedInvitationSchema } from "./members.js";
// ---------------------------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------------------------
export const LEAD_STAGES = ['new', 'contacted', 'replied', 'demo', 'proposal', 'won', 'lost', 'parked'];
export const LeadStageSchema = z.enum(LEAD_STAGES).openapi('LeadStage', {
    description: 'Where a lead is. `new` → `contacted` → `replied` → `demo` → `proposal` → `won`; `lost` (with a reason) and `parked` from any open stage.',
});
/** The stages a lead is still being worked in. */
export const OPEN_LEAD_STAGES = ['new', 'contacted', 'replied', 'demo', 'proposal'];
export const LEAD_LOST_REASONS = ['price', 'happy-with-current-software', 'closed-down', 'no-reply', 'not-a-fit', 'other'];
export const LeadLostReasonSchema = z.enum(LEAD_LOST_REASONS).openapi('LeadLostReason');
export const LEAD_PRIORITIES = ['a', 'b', 'c'];
export const LeadPrioritySchema = z.enum(LEAD_PRIORITIES).openapi('LeadPriority', { description: '`a` is the best prospect. `c` is the default.' });
export const LEAD_SOURCES = ['manual', 'import', 'website'];
export const LeadSourceSchema = z.enum(LEAD_SOURCES).openapi('LeadSource');
export const LEAD_ACTIVITY_KINDS = [
    'message',
    'call',
    'visit',
    'reply',
    'note',
    'stage_change',
    'import',
    'follow_up',
    'do_not_contact',
    'conversion',
];
export const LeadActivityKindSchema = z.enum(LEAD_ACTIVITY_KINDS).openapi('LeadActivityKind');
/** The kinds a member logs by hand; the rest are written by the server. */
export const LOGGABLE_LEAD_ACTIVITY_KINDS = ['note', 'call', 'visit', 'reply'];
export const LEAD_CHANNELS = ['whatsapp', 'instagram', 'email', 'call', 'visit'];
export const LeadChannelSchema = z.enum(LEAD_CHANNELS).openapi('LeadChannel');
/** The channels a template or an outreach message uses. */
export const LEAD_MESSAGE_CHANNELS = ['whatsapp', 'instagram', 'email'];
export const LeadMessageChannelSchema = z.enum(LEAD_MESSAGE_CHANNELS).openapi('LeadMessageChannel');
export const LEAD_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const LEAD_MAX_IMPORT_ROWS = 2_000;
export const LEAD_MAX_MESSAGE_CHARACTERS = 2_000;
// ---------------------------------------------------------------------------------------------
// The shapes
// ---------------------------------------------------------------------------------------------
const UserIdSchema = z.uuid().openapi({ description: 'An HQ member’s user id.' });
export const LeadSummarySchema = z
    .object({
    id: LeadIdSchema,
    businessName: z.string(),
    city: z.string().nullable(),
    area: z.string().nullable(),
    /** E.164. */
    phone: z.string().nullable(),
    /** False for a landline, or when there is no phone. */
    phoneMobile: z.boolean(),
    instagram: z.string().nullable(),
    priority: LeadPrioritySchema,
    stage: LeadStageSchema,
    ownerUserId: UserIdSchema.nullable(),
    ownerName: z.string().nullable(),
    nextFollowUpAt: IsoDateTimeSchema.nullable(),
    lastContactedAt: IsoDateTimeSchema.nullable(),
    /** Tenths: `46` is 4.6. */
    googleRatingTenths: z.int().min(0).max(50).nullable(),
    googleReviewCount: z.int().min(0).nullable(),
    doNotContact: z.boolean(),
    organizationId: OrganizationIdSchema.nullable(),
})
    .openapi('LeadSummary', { description: 'One row of the pipeline.' });
export const LeadSchema = z
    .object({
    id: LeadIdSchema,
    businessName: z.string(),
    contactName: z.string().nullable(),
    phone: z.string().nullable(),
    phoneMobile: z.boolean(),
    email: z.string().nullable(),
    /** The handle, without `@`. */
    instagram: z.string().nullable(),
    city: z.string().nullable(),
    area: z.string().nullable(),
    addressHint: z.string().nullable(),
    stationCount: z.int().min(0).nullable(),
    platforms: z.array(z.string()),
    currentSoftware: z.string().nullable(),
    googleRatingTenths: z.int().min(0).max(50).nullable(),
    googleReviewCount: z.int().min(0).nullable(),
    mapUrl: z.string().nullable(),
    hook: z.string().nullable(),
    priority: LeadPrioritySchema,
    stage: LeadStageSchema,
    lostReason: LeadLostReasonSchema.nullable(),
    ownerUserId: UserIdSchema.nullable(),
    ownerName: z.string().nullable(),
    nextFollowUpAt: IsoDateTimeSchema.nullable(),
    lastContactedAt: IsoDateTimeSchema.nullable(),
    doNotContact: z.boolean(),
    doNotContactAt: IsoDateTimeSchema.nullable(),
    doNotContactReason: z.string().nullable(),
    source: LeadSourceSchema,
    /** The organisation this lead became; `null` until converted. */
    organizationId: OrganizationIdSchema.nullable(),
    organizationName: z.string().nullable(),
    createdByUserId: UserIdSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('Lead', { description: 'A café Clavian is trying to sign (spec/14 §16).' });
export const LeadActivitySchema = z
    .object({
    id: LeadActivityIdSchema,
    kind: LeadActivityKindSchema,
    channel: LeadChannelSchema.nullable(),
    templateId: LeadTemplateIdSchema.nullable(),
    templateName: z.string().nullable(),
    /** True for the first message to a `new` lead (the kind the daily cap counts). */
    firstContact: z.boolean(),
    body: z.string().nullable(),
    fromStage: LeadStageSchema.nullable(),
    toStage: LeadStageSchema.nullable(),
    actorUserId: UserIdSchema.nullable(),
    actorName: z.string().nullable(),
    occurredAt: IsoDateTimeSchema,
    createdAt: IsoDateTimeSchema,
})
    .openapi('LeadActivity', { description: 'One entry of a lead’s timeline. Never edited: corrected by a new note.' });
export const LeadListSchema = z
    .object({
    data: z.array(LeadSummarySchema),
    page: PageSchema,
    /** Every lead the filters match, not just this page: the count a column or table header shows. */
    total: z.int().min(0),
})
    .openapi('LeadList');
export const LeadActivityListSchema = paginated(LeadActivitySchema, 'LeadActivityList');
const nullableText = (max) => z.string().trim().max(max).nullable().optional();
const HttpUrlSchema = z
    .string()
    .trim()
    .max(500)
    .regex(/^https?:\/\/\S+$/i, 'An http or https link');
/** Fields common to creating and editing a lead. */
const leadFields = {
    contactName: nullableText(120),
    /** As written: `098765 43210`, `+91 98765 43210`. Stored as E.164; anything that is not an Indian number is `400`. */
    phone: nullableText(40),
    email: z.string().trim().max(254).regex(/^[^\s@]+@[^\s@]+$/, 'An email address').nullable().optional(),
    /** A handle, `@handle` or an instagram.com link. Stored as the bare handle. */
    instagram: nullableText(120),
    city: nullableText(80),
    area: nullableText(120),
    addressHint: nullableText(300),
    stationCount: z.int().min(0).max(1000).nullable().optional(),
    platforms: z.array(z.string().trim().min(1).max(40)).max(10).optional(),
    currentSoftware: nullableText(120),
    googleRatingTenths: z.int().min(0).max(50).nullable().optional(),
    googleReviewCount: z.int().min(0).max(10_000_000).nullable().optional(),
    mapUrl: HttpUrlSchema.nullable().optional(),
    hook: nullableText(500),
    priority: LeadPrioritySchema.optional(),
    ownerUserId: UserIdSchema.nullable().optional(),
    nextFollowUpAt: IsoDateTimeSchema.nullable().optional(),
};
export const CreateLeadSchema = z
    .object({ businessName: z.string().trim().min(1).max(200), ...leadFields })
    .strict()
    .openapi('CreateLead', { description: 'A new lead: the business name is all that is required.' });
export const UpdateLeadSchema = z
    .object({
    businessName: z.string().trim().min(1).max(200).optional(),
    ...leadFields,
    /** Setting it needs `doNotContactReason`; clearing it ends the mark. Each change is on the timeline and audited. */
    doNotContact: z.boolean().optional(),
    doNotContactReason: z.string().trim().min(1).max(300).optional(),
})
    .strict()
    .openapi('UpdateLead', { description: 'Any editable field; `null` clears an optional one.' });
export const ChangeLeadStageSchema = z
    .object({
    stage: LeadStageSchema,
    /** Required when `stage` is `lost`. */
    lostReason: LeadLostReasonSchema.optional(),
    note: z.string().trim().min(1).max(LEAD_MAX_MESSAGE_CHARACTERS).optional(),
})
    .strict()
    .openapi('ChangeLeadStage');
export const LogLeadActivitySchema = z
    .object({
    kind: z.enum(LOGGABLE_LEAD_ACTIVITY_KINDS),
    channel: LeadChannelSchema.optional(),
    /** Required for a note. */
    body: z.string().trim().min(1).max(LEAD_MAX_MESSAGE_CHARACTERS).optional(),
    /** When it happened; not in the future. Defaults to now. */
    occurredAt: IsoDateTimeSchema.optional(),
})
    .strict()
    .refine((body) => body.kind !== 'note' || body.body !== undefined, { message: 'A note needs its text.', path: ['body'] })
    .openapi('LogLeadActivity', {
    description: 'A note, call, visit or reply. A reply on a `contacted` lead moves it to `replied`; a call or visit sets *last contacted*.',
});
export const OpenLeadOutreachSchema = z
    .object({
    channel: LeadMessageChannelSchema,
    templateId: LeadTemplateIdSchema.optional(),
    /** The text as the member finally edited it. */
    text: z.string().trim().min(1).max(LEAD_MAX_MESSAGE_CHARACTERS),
})
    .strict()
    .openapi('OpenLeadOutreach');
export const LeadOutreachSchema = z
    .object({
    lead: LeadSchema,
    activity: LeadActivitySchema,
    /** `https://api.whatsapp.com/send?phone=…&text=…` for the WhatsApp channel; `null` otherwise. The member opens it; HQ sends nothing. */
    whatsAppUrl: z.string().nullable(),
    /** The profile address for the Instagram channel; `null` otherwise. */
    instagramUrl: z.string().nullable(),
    /** First messages this member can still open today (the cap less today’s). */
    firstMessagesLeftToday: z.int().min(0),
})
    .openapi('LeadOutreach', { description: 'The message recorded on the timeline, and the link to open it from.' });
export const ConvertLeadSchema = z
    .object({
    /** The organisation’s business name; the lead’s, editable first. */
    name: z.string().trim().min(1).max(120),
    owner: OwnerInvitationContactSchema,
    kind: OrganizationKindSchema.optional(),
})
    .strict()
    .openapi('ConvertLead', {
    description: 'Make the lead an organisation through the light invite (ADR-0046). Also needs `hq:organizations`.',
});
export const ConvertedLeadSchema = z
    .object({
    lead: LeadSchema,
    organization: HqOrganizationSchema,
    ownerInvitation: CreatedInvitationSchema,
})
    .openapi('ConvertedLead', { description: 'The lead (now `won`, linked), the organisation just created and its owner’s invitation.' });
// Import ------------------------------------------------------------------------------------------
export const ImportLeadsSchema = z
    .object({
    fileName: z.string().min(1).max(200),
    contentBase64: z.string().min(4).max(Math.ceil((LEAD_MAX_FILE_BYTES * 4) / 3) + 4),
    /** True: report what would happen and write nothing. */
    dryRun: z.boolean(),
})
    .strict()
    .openapi('ImportLeads');
export const LeadImportDuplicateSchema = z
    .object({ row: z.int().min(1), businessName: z.string(), reason: z.string() })
    .openapi('LeadImportDuplicate', { description: 'A row skipped because the lead already exists (or appeared earlier in the file).' });
export const LeadImportRefusedSchema = z
    .object({ row: z.int().min(1), reason: z.string() })
    .openapi('LeadImportRefused', { description: 'A row that could not be read as a lead.' });
export const LeadImportColumnsSchema = z
    .object({ matched: z.array(z.string()), ignored: z.array(z.string()) })
    .openapi('LeadImportColumns', { description: 'The file’s headers: the ones that were recognised, and the ones ignored.' });
export const LeadImportResultSchema = z
    .object({
    /** Data rows in the file. */
    total: z.int().min(0),
    /** Leads added; on a dry run, the number that would be. */
    added: z.int().min(0),
    duplicates: z.array(LeadImportDuplicateSchema),
    refused: z.array(LeadImportRefusedSchema),
    columns: LeadImportColumnsSchema,
})
    .openapi('LeadImportResult', { description: 'Row numbers are the spreadsheet’s: the first data row under the header is row 2.' });
// Queue, funnel, filters ------------------------------------------------------------------------------
export const LeadQueueSchema = z
    .object({
    /** Open leads with a follow-up due by the end of today (deployment time zone), not do-not-contact; oldest first; at most 50. */
    due: z.array(LeadSummarySchema),
    /** `new` leads with a phone or Instagram handle, not do-not-contact; priority a→c then most reviews; at most 50. */
    fresh: z.array(LeadSummarySchema),
    firstMessagesLeftToday: z.int().min(0),
    dailyCap: z.int().min(1),
})
    .openapi('LeadQueue');
export const LeadFunnelStageSchema = z.object({ stage: LeadStageSchema, count: z.int().min(0) }).openapi('LeadFunnelStage');
export const LeadFunnelCitySchema = z
    .object({ city: z.string(), total: z.int().min(0), open: z.int().min(0), won: z.int().min(0) })
    .openapi('LeadFunnelCity');
export const LeadFunnelTemplateSchema = z
    .object({
    templateId: LeadTemplateIdSchema.nullable(),
    templateName: z.string().nullable(),
    channel: LeadMessageChannelSchema,
    sent: z.int().min(0),
    replied: z.int().min(0),
})
    .openapi('LeadFunnelTemplate', { description: 'Messages sent with one template on one channel, and the replies credited to them. `templateId` is null for text written without a template.' });
export const LeadFunnelTotalsSchema = z
    .object({ leads: z.int().min(0), contacted: z.int().min(0), replied: z.int().min(0), won: z.int().min(0) })
    .openapi('LeadFunnelTotals');
export const LeadFunnelSchema = z
    .object({
    byStage: z.array(LeadFunnelStageSchema),
    byCity: z.array(LeadFunnelCitySchema),
    byTemplate: z.array(LeadFunnelTemplateSchema),
    totals: LeadFunnelTotalsSchema,
})
    .openapi('LeadFunnel', {
    description: 'A reply is credited to the latest message before it on the same lead. `contacted` and `replied` count leads that ever were.',
});
export const LeadCityCountSchema = z.object({ city: z.string(), count: z.int().min(0) }).openapi('LeadCityCount');
export const LeadCityListSchema = z
    .object({ data: z.array(LeadCityCountSchema) })
    .openapi('LeadCityList');
export const LeadAssigneeSchema = z.object({ id: UserIdSchema, name: z.string(), role: z.string() }).openapi('LeadAssignee');
export const LeadAssigneeListSchema = z
    .object({ data: z.array(LeadAssigneeSchema) })
    .openapi('LeadAssigneeList', { description: 'HQ members whose platform role holds `hq:leads`.' });
// Templates -------------------------------------------------------------------------------------------
export const LeadTemplateSchema = z
    .object({
    id: LeadTemplateIdSchema,
    name: z.string(),
    channel: LeadMessageChannelSchema,
    /** With `{{business}}`, `{{contact}}`, `{{city}}`, `{{area}}`, `{{hook}}`, `{{sender}}` placeholders. */
    body: z.string(),
    archivedAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('LeadTemplate');
export const LeadTemplateListSchema = z.object({ data: z.array(LeadTemplateSchema) }).openapi('LeadTemplateList');
export const CreateLeadTemplateSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    channel: LeadMessageChannelSchema,
    body: z.string().trim().min(1).max(LEAD_MAX_MESSAGE_CHARACTERS),
})
    .strict()
    .openapi('CreateLeadTemplate');
export const UpdateLeadTemplateSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    channel: LeadMessageChannelSchema.optional(),
    body: z.string().trim().min(1).max(LEAD_MAX_MESSAGE_CHARACTERS).optional(),
})
    .strict()
    .openapi('UpdateLeadTemplate');
// Queries and params ----------------------------------------------------------------------------------
export const LEAD_LIST_SORTS = ['followUp', 'reviews', 'updated'];
export const LeadListQuerySchema = PageQuerySchema.extend({
    stage: z
        .enum([...LEAD_STAGES, 'open'])
        .optional()
        .openapi({ description: 'One stage, or `open` for every open stage.' }),
    city: z.string().min(1).max(80).optional(),
    priority: LeadPrioritySchema.optional(),
    ownerId: UserIdSchema.optional(),
    doNotContact: z.enum(['true', 'false']).optional(),
    q: z.string().min(1).max(120).optional().openapi({ description: 'Matches the business name, area, Instagram handle and phone digits.' }),
    sort: z
        .enum(LEAD_LIST_SORTS)
        .default('followUp')
        .openapi({ description: '`followUp`: next follow-up (soonest first, none last) then most reviews. `reviews`: most reviews. `updated`: most recently changed.' }),
})
    .strict()
    .openapi('LeadListQuery');
export const LeadActivityListQuerySchema = PageQuerySchema.strict().openapi('LeadActivityListQuery');
export const LeadTemplateListQuerySchema = z
    .object({ includeArchived: z.enum(['true', 'false']).optional() })
    .strict()
    .openapi('LeadTemplateListQuery');
export const LeadParamsSchema = z.object({ leadId: LeadIdSchema }).openapi('LeadParams');
export const LeadTemplateParamsSchema = z.object({ templateId: LeadTemplateIdSchema }).openapi('LeadTemplateParams');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const json = (schema) => ({
    body: { content: { 'application/json': { schema } }, required: true },
});
const forbidden = problemResponse('Not HQ, no `hq:leads`, or no second factor on the session.', 'forbidden', 'second-factor-required');
const noSuchLead = problemResponse('No such lead.', 'not-found');
const noSuchTemplate = problemResponse('No such template.', 'not-found');
const failures = (extra = {}) => ({
    400: commonProblemResponses[400],
    401: commonProblemResponses[401],
    403: forbidden,
    429: commonProblemResponses[429],
    500: commonProblemResponses[500],
    ...extra,
});
const responses = (schema, description, extra = {}) => ({
    200: jsonResponse(schema, description),
    ...failures(extra),
});
const created = (schema, description, extra = {}) => ({
    201: jsonResponse(schema, description),
    ...failures(extra),
});
export const listHqLeadsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/leads',
    operationId: 'listHqLeads',
    tags: ['hq'],
    summary: 'The pipeline: leads, filtered and sorted',
    description: 'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { query: LeadListQuerySchema },
    responses: responses(LeadListSchema, 'A page of leads.'),
});
export const createHqLeadRoute = createRoute({
    method: 'post',
    path: '/v1/hq/leads',
    operationId: 'createHqLead',
    tags: ['hq'],
    summary: 'Add a lead',
    description: 'The business name is all that is required. A phone number is read as written and stored as E.164; one that is not an Indian number is ' +
        '`400` on `phone`, and one another lead already has is `409 lead-phone-taken`. Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { ...json(CreateLeadSchema) },
    responses: created(LeadSchema, 'The lead.', { 409: problemResponse('Another lead has that phone number.', 'lead-phone-taken') }),
});
export const importHqLeadsRoute = createRoute({
    method: 'post',
    path: '/v1/hq/leads/import',
    operationId: 'importHqLeads',
    tags: ['hq'],
    summary: 'Import the sales spreadsheet (CSV or XLSX)',
    description: `The bytes as base64, at most ${LEAD_MAX_FILE_BYTES / 1024 / 1024} MB and ${LEAD_MAX_IMPORT_ROWS} rows; read on the server and never executed. ` +
        'Columns are matched by header, ignoring case and spacing. A row with no business name is refused. A duplicate — the phone is already a lead ' +
        '(or appears earlier in the file), or, with no phone, the same name in the same city — is skipped and listed, never overwritten. ' +
        '`dryRun: true` writes nothing; `false` adds the leads in one transaction and writes one audit row. Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { ...json(ImportLeadsSchema) },
    responses: responses(LeadImportResultSchema, 'What was (or would be) added, skipped and refused.', {
        422: problemResponse('The file is too large, cannot be read as CSV or XLSX, or has too many rows.', 'migration-file-unreadable'),
    }),
});
export const listHqLeadCitiesRoute = createRoute({
    method: 'get',
    path: '/v1/hq/leads/cities',
    operationId: 'listHqLeadCities',
    tags: ['hq'],
    summary: 'Cities that have leads, with counts',
    description: 'For the filter chips. Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    responses: responses(LeadCityListSchema, 'Cities, most leads first.'),
});
export const getHqLeadQueueRoute = createRoute({
    method: 'get',
    path: '/v1/hq/leads/queue',
    operationId: 'getHqLeadQueue',
    tags: ['hq'],
    summary: 'Today: follow-ups due and new leads to contact',
    description: 'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    responses: responses(LeadQueueSchema, 'The queue, and how many first messages the caller has left today.'),
});
export const getHqLeadFunnelRoute = createRoute({
    method: 'get',
    path: '/v1/hq/leads/funnel',
    operationId: 'getHqLeadFunnel',
    tags: ['hq'],
    summary: 'The funnel: leads by stage and city, replies by template',
    description: 'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    responses: responses(LeadFunnelSchema, 'The funnel.'),
});
export const listHqLeadAssigneesRoute = createRoute({
    method: 'get',
    path: '/v1/hq/leads/assignees',
    operationId: 'listHqLeadAssignees',
    tags: ['hq'],
    summary: 'HQ members who can own a lead',
    description: 'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    responses: responses(LeadAssigneeListSchema, 'HQ members whose role holds `hq:leads`.'),
});
export const getHqLeadRoute = createRoute({
    method: 'get',
    path: '/v1/hq/leads/{leadId}',
    operationId: 'getHqLead',
    tags: ['hq'],
    summary: 'One lead',
    description: 'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadParamsSchema },
    responses: responses(LeadSchema, 'The lead.', { 404: noSuchLead }),
});
export const updateHqLeadRoute = createRoute({
    method: 'patch',
    path: '/v1/hq/leads/{leadId}',
    operationId: 'updateHqLead',
    tags: ['hq'],
    summary: 'Edit a lead',
    description: 'Any editable field. The owner must be an HQ member who holds `hq:leads`. Setting a next follow-up writes a timeline entry. Marking do-not-contact ' +
        'needs a reason; setting or clearing it writes a timeline entry and an audit row. Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadParamsSchema, ...json(UpdateLeadSchema) },
    responses: responses(LeadSchema, 'The lead as it is now.', {
        404: noSuchLead,
        409: problemResponse('Another lead has that phone number.', 'lead-phone-taken'),
    }),
});
export const deleteHqLeadRoute = createRoute({
    method: 'delete',
    path: '/v1/hq/leads/{leadId}',
    operationId: 'deleteHqLead',
    tags: ['hq'],
    summary: 'Delete a lead and its timeline',
    description: 'For when the person asks for their details to be erased. One audit row, with the lead’s details masked. Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadParamsSchema },
    responses: { 204: { description: 'Deleted.' }, ...failures({ 404: noSuchLead }) },
});
export const changeHqLeadStageRoute = createRoute({
    method: 'post',
    path: '/v1/hq/leads/{leadId}/stage',
    operationId: 'changeHqLeadStage',
    tags: ['hq'],
    summary: 'Move a lead to another stage',
    description: 'An open stage goes to any other stage; a `won`, `lost` or `parked` lead can be reopened to an open stage. `lost` needs `lostReason`. Writes a timeline entry. ' +
        'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadParamsSchema, ...json(ChangeLeadStageSchema) },
    responses: responses(LeadSchema, 'The lead as it is now.', {
        404: noSuchLead,
        409: problemResponse('That move is not allowed from the lead’s stage.', 'conflict'),
    }),
});
export const listHqLeadActivitiesRoute = createRoute({
    method: 'get',
    path: '/v1/hq/leads/{leadId}/activities',
    operationId: 'listHqLeadActivities',
    tags: ['hq'],
    summary: 'A lead’s timeline, newest first',
    description: 'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadParamsSchema, query: LeadActivityListQuerySchema },
    responses: responses(LeadActivityListSchema, 'A page of timeline entries.', { 404: noSuchLead }),
});
export const logHqLeadActivityRoute = createRoute({
    method: 'post',
    path: '/v1/hq/leads/{leadId}/activities',
    operationId: 'logHqLeadActivity',
    tags: ['hq'],
    summary: 'Log a note, call, visit or reply',
    description: 'A reply on a `contacted` lead moves it to `replied` (with its own timeline entry); a call or visit sets *last contacted*. `occurredAt` cannot be in the future. ' +
        'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadParamsSchema, ...json(LogLeadActivitySchema) },
    responses: created(LeadActivitySchema, 'The entry.', { 404: noSuchLead }),
});
export const openHqLeadOutreachRoute = createRoute({
    method: 'post',
    path: '/v1/hq/leads/{leadId}/outreach',
    operationId: 'openHqLeadOutreach',
    tags: ['hq'],
    summary: 'Record a message and get the link to send it from',
    description: 'HQ never sends: this records the message on the timeline, sets *last contacted*, moves a `new` lead to `contacted` and sets the next follow-up ' +
        '`leads.followUpDays` ahead unless one is set, and returns the WhatsApp link (WhatsApp) or profile address (Instagram) for the member to open. Refused, in this ' +
        'order: do-not-contact (`409 do-not-contact`), WhatsApp with no phone (`lead-no-phone`), Instagram with no handle (`lead-no-instagram`), and a first message ' +
        'over the member’s daily cap (`daily-outreach-cap`; follow-ups do not count). Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadParamsSchema, ...json(OpenLeadOutreachSchema) },
    responses: created(LeadOutreachSchema, 'The recorded message and its link.', {
        404: noSuchLead,
        409: problemResponse('The lead cannot be contacted this way, or the daily cap is reached.', 'do-not-contact', 'lead-no-phone', 'lead-no-instagram', 'daily-outreach-cap'),
    }),
});
export const convertHqLeadRoute = createRoute({
    method: 'post',
    path: '/v1/hq/leads/{leadId}/convert',
    operationId: 'convertHqLead',
    tags: ['hq'],
    summary: 'Convert a lead into an organisation',
    description: 'Creates the organisation through the light invite (`POST /v1/hq/organizations`), links the lead to it, moves it to `won`, and writes the conversion on the ' +
        'timeline and one audit row. Needs `hq:organizations` as well as `hq:leads` (`403` without it). A lead that already has an organisation is ' +
        '`409 lead-converted`; if the organisation cannot be created (such as `slug-taken`) the lead is unchanged. Platform scope.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadParamsSchema, ...json(ConvertLeadSchema) },
    responses: created(ConvertedLeadSchema, 'The lead, the organisation and its owner’s invitation.', {
        404: noSuchLead,
        409: problemResponse('The lead already has an organisation, or the organisation could not be created.', 'lead-converted', 'slug-taken', 'phone-invitations-off'),
    }),
});
export const listHqLeadTemplatesRoute = createRoute({
    method: 'get',
    path: '/v1/hq/lead-templates',
    operationId: 'listHqLeadTemplates',
    tags: ['hq'],
    summary: 'Message templates',
    description: 'Archived ones only with `includeArchived=true`. Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { query: LeadTemplateListQuerySchema },
    responses: responses(LeadTemplateListSchema, 'Templates, oldest first.'),
});
export const createHqLeadTemplateRoute = createRoute({
    method: 'post',
    path: '/v1/hq/lead-templates',
    operationId: 'createHqLeadTemplate',
    tags: ['hq'],
    summary: 'Add a message template',
    description: 'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { ...json(CreateLeadTemplateSchema) },
    responses: created(LeadTemplateSchema, 'The template.'),
});
export const updateHqLeadTemplateRoute = createRoute({
    method: 'patch',
    path: '/v1/hq/lead-templates/{templateId}',
    operationId: 'updateHqLeadTemplate',
    tags: ['hq'],
    summary: 'Edit a message template',
    description: 'Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadTemplateParamsSchema, ...json(UpdateLeadTemplateSchema) },
    responses: responses(LeadTemplateSchema, 'The template as it is now.', { 404: noSuchTemplate }),
});
export const archiveHqLeadTemplateRoute = createRoute({
    method: 'post',
    path: '/v1/hq/lead-templates/{templateId}/archive',
    operationId: 'archiveHqLeadTemplate',
    tags: ['hq'],
    summary: 'Archive a message template',
    description: 'Archived, never deleted, so the timeline keeps what each message used. Archiving an archived template changes nothing. Platform scope: requires `hq:leads` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_LEADS,
    request: { params: LeadTemplateParamsSchema },
    responses: responses(LeadTemplateSchema, 'The template, archived.', { 404: noSuchTemplate }),
});
// Static paths before `{leadId}`: the order the server registers them in.
export const HQ_LEAD_ROUTES = [
    listHqLeadsRoute,
    createHqLeadRoute,
    importHqLeadsRoute,
    listHqLeadCitiesRoute,
    getHqLeadQueueRoute,
    getHqLeadFunnelRoute,
    listHqLeadAssigneesRoute,
    getHqLeadRoute,
    updateHqLeadRoute,
    deleteHqLeadRoute,
    changeHqLeadStageRoute,
    listHqLeadActivitiesRoute,
    logHqLeadActivityRoute,
    openHqLeadOutreachRoute,
    convertHqLeadRoute,
    listHqLeadTemplatesRoute,
    createHqLeadTemplateRoute,
    updateHqLeadTemplateRoute,
    archiveHqLeadTemplateRoute,
];
//# sourceMappingURL=hq-leads.js.map